import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { useCallback, useRef } from "react"

import { cn } from "@/lib/utils"

type DragHandleVariant = "compact" | "header"

/** Drag handle that slides the sheet down as you drag and closes past threshold.
 *  Tap alone does nothing — must drag down deliberately to dismiss. */
function DragHandle({
  onClose,
  variant = "compact",
}: {
  onClose: () => void
  variant?: DragHandleVariant
}) {
  const startY = useRef(0)
  const maxDy = useRef(0)
  const dragging = useRef(false)
  const sheetRef = useRef<HTMLElement | null>(null)

  const getSheet = useCallback((el: HTMLElement | null) => {
    if (el) sheetRef.current = el.closest('[data-state]') as HTMLElement
  }, [])

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === 'touch') e.preventDefault()
    startY.current = e.clientY
    maxDy.current = 0
    dragging.current = true
    e.currentTarget.setPointerCapture(e.pointerId)
    const sheet = sheetRef.current
    if (sheet) sheet.style.transition = 'none'
  }, [])

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return
    if (e.pointerType === 'touch') e.preventDefault()
    const dy = Math.max(0, e.clientY - startY.current)
    maxDy.current = Math.max(maxDy.current, dy)
    const sheet = sheetRef.current
    if (sheet) {
      sheet.style.transform = `translate3d(0, ${dy}px, 0)`
      const overlay = sheet.previousElementSibling as HTMLElement | null
      if (overlay) overlay.style.opacity = String(Math.max(0, 1 - dy / 300))
    }
  }, [])

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return
    dragging.current = false
    const dy = e.clientY - startY.current
    const maxDragDistance = Math.max(maxDy.current, dy)
    const sheet = sheetRef.current
    const overlay = sheet?.previousElementSibling as HTMLElement | null

    if (maxDragDistance > 120) {
      // Past threshold — animate out and close
      if (sheet) {
        sheet.style.transition = 'transform 0.2s ease-out'
        sheet.style.transform = `translate3d(0, 100%, 0)`
      }
      if (overlay) {
        overlay.style.transition = 'opacity 0.2s ease-out'
        overlay.style.opacity = '0'
      }
      setTimeout(onClose, 200)
    } else {
      // Snap back
      if (sheet) {
        sheet.style.transition = 'transform 0.25s cubic-bezier(0.25, 0.1, 0.25, 1)'
        sheet.style.transform = ''
      }
      if (overlay) {
        overlay.style.transition = 'opacity 0.25s ease-out'
        overlay.style.opacity = ''
      }
    }
  }, [onClose])

  const onPointerCancel = useCallback(() => {
    dragging.current = false
    const sheet = sheetRef.current
    if (sheet) {
      sheet.style.transition = ''
      sheet.style.transform = ''
    }
  }, [])

  return (
    <div
      ref={getSheet}
      className={cn(
        "items-center justify-center cursor-grab active:cursor-grabbing touch-none",
        variant === "header" ? "flex h-12 w-full" : "mx-auto flex h-10 w-16 rounded"
      )}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      role="separator"
      aria-label="Drag down to close"
    >
      <div className="h-[3px] w-8 rounded-full bg-muted-foreground/30" />
    </div>
  )
}

const Sheet = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
    ref={ref}
  />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  "fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t pt-1.5 gap-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4  border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  }
)

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
  VariantProps<typeof sheetVariants> {
  /** Called when the sheet should close (overlay click, escape, or handle drag) */
  onClose?: () => void
  /** Controls touch target size for the bottom-sheet drag handle */
  dragHandleVariant?: DragHandleVariant
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = "right", className, children, onClose, dragHandleVariant = "compact", ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay />
    <SheetPrimitive.Content
      ref={ref}
      className={cn(sheetVariants({ side }), className)}
      {...props}
    >
      {/* Bottom sheets get a drag handle; side sheets get nothing (consumers add their own close) */}
      {side === "bottom" && onClose && <DragHandle onClose={onClose} variant={dragHandleVariant} />}
      {children}
    </SheetPrimitive.Content>
  </SheetPortal>
))
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
SheetHeader.displayName = "SheetHeader"

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet, SheetClose,
  SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetOverlay, SheetPortal, SheetTitle, SheetTrigger
}
